/**
 * @fileoverview Provide the browser's shared vocabulary for Intake stages and statuses. Views use these labels/options to present backend state consistently; the backend owns valid transitions and persisted decisions. `intakeFormatters.js` owns general presentation and `Queue/intakeQueueUtils.js` owns queue projections.
 */

// Status vocabularies for intake leads, pipeline/stage/agent runs, and outreach drafts.
// These mirror the status strings emitted by the Flask intake API; keep them in sync
// with kc-internal-backend when statuses are added or renamed.
export const ATTENTION_STATUSES = new Set(['failed', 'error', 'stale']);
export const ACTIVE_STATUSES = new Set(['pending', 'queued', 'waiting', 'running', 'processing']);
export const COMPLETE_STATUSES = new Set(['completed', 'approved', 'accepted', 'reviewed', 'converted']);
export const CLEARED_STATUSES = new Set(['cleared']);
export const FAILED_RUN_STATUSES = new Set(['failed', 'error']);
export const QUEUED_RUN_STATUSES = new Set(['pending', 'queued', 'waiting']);
export const RUNNING_RUN_STATUSES = new Set(['running', 'processing']);
export const NO_ACTION_RUN_STATUSES = new Set(['skipped']);
export const REVIEW_RUN_STATUSES = new Set(['needs_review', 'review_required']);
export const CLOSED_LEAD_STATUSES = new Set(['converted', 'rejected', 'archived']);
export const TERMINAL_NON_PROGRESS_STATUSES = new Set(['rejected', 'archived']);
export const UNRESOLVED_DUPLICATE_STATUSES = new Set(['likely_duplicate', 'possible_duplicate']);
export const FINAL_RECOMMENDATION_NOT_VIABLE_TOKENS = new Set(['not_viable', 'not_viable_case', 'non_viable', 'non_viable_case', 'rejected', 'reject', 'auto_rejected', 'auto_reject']);
export const FINAL_RECOMMENDATION_NEEDS_INFO_TOKENS = new Set(['needs_more_info', 'need_more_info', 'more_info_needed', 'request_more_info', 'information_needed', 'info_needed']);
export const FINAL_RECOMMENDATION_HUMAN_REVIEW_TOKENS = new Set(['human_review', 'attorney_review', 'case_review', 'firm_review', 'review']);
export const FINAL_RECOMMENDATION_VIABLE_TOKENS = new Set(['viable', 'accepted', 'accept', 'approve', 'approved']);
export const DEDUPE_REVIEW_TITLE = 'Duplicate Review Required';

export const EDITABLE_OUTREACH_STATUSES = new Set(['draft', 'approved', 'send_failed']);
export const SEND_BLOCKED_OUTREACH_STATUSES = new Set(['sent', 'skipped', 'sending']);



export function getRunStatusTone(status) {
  const normalized = String(status || '').toLowerCase();
  if (ATTENTION_STATUSES.has(normalized) || FAILED_RUN_STATUSES.has(normalized)) return 'danger';
  if (REVIEW_RUN_STATUSES.has(normalized)) return 'warning';
  if (ACTIVE_STATUSES.has(normalized)) return 'active';
  if (COMPLETE_STATUSES.has(normalized)) return 'success';
  if (NO_ACTION_RUN_STATUSES.has(normalized) || CLEARED_STATUSES.has(normalized)) return 'muted';
  return 'neutral';
}

// Dedupe-agent review lifecycle (duplicate-candidate advisory runs on the lead detail page).
export const FAILED_AGENT_STATUSES = new Set(['failed', 'error']);
export const COMPLETED_AGENT_STATUSES = new Set(['completed', 'approved', 'reviewed']);
export const ACTIVE_AGENT_STATUSES = new Set(['pending', 'queued', 'running']);
